import { useEffect, useState } from 'react';
import type { SimBridge, HudState } from '../../state/simBridge';
import type { CarSnapshot, CarSpec, Gear } from '../../sim/types';
import { PIP_RECTS, type PipRect } from '../../engine/PipView';
import { REDLINE_RPM } from '../../sim/drivetrain';
import { useT, useSpeedFormat } from '../hooks';
import { BigButton } from '../components';
import type { GameControls } from './GameScreen';
import type { TouchSource } from '../../controls/TouchSource';
import { MobileControls } from './MobileControls';
import { Tacho } from './Tacho';
import { HShifter } from './HShifter';

const AUTO_GEARS: Gear[] = ['P', 'R', 'N', 'D'];

function pipStyle(rect: PipRect): React.CSSProperties {
  return {
    left: `${rect.x * 100}%`,
    bottom: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
  };
}

function Light({ on, color, children }: { on: boolean; color: string; children: React.ReactNode }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide transition ${
        on ? color : 'bg-white/5 text-white/25'
      }`}
    >
      {children}
    </span>
  );
}

/** Barra de pedal vertical. El embrague pinta su zona de fricción. */
function PedalBar({
  value,
  color,
  label,
  bite,
  atBite,
}: {
  value: number;
  color: string;
  label: string;
  /** [desde, hasta] de la zona de fricción como fracción del recorrido. */
  bite?: [number, number];
  atBite?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="relative h-16 w-4 overflow-hidden rounded-full bg-white/10">
        <div
          className={`absolute bottom-0 left-0 w-full rounded-b-full ${color}`}
          style={{ height: `${Math.round(value * 100)}%` }}
        />
        {bite && (
          <div
            className={`absolute left-0 w-full ${
              atBite ? 'bg-emerald-300/80 shadow-[0_0_8px_2px_rgba(52,211,153,0.8)]' : 'bg-white/30'
            }`}
            style={{
              bottom: `${Math.round(bite[0] * 100)}%`,
              height: `${Math.round((bite[1] - bite[0]) * 100)}%`,
            }}
          />
        )}
      </div>
      <span className="text-[9px] font-bold tracking-wider text-white/50">{label}</span>
    </div>
  );
}

function gearLabelOf(snap: CarSnapshot, isManual: boolean, multiRatio: boolean): string {
  if (isManual) return String(snap.gear);
  if (snap.gear === 'D' && multiRatio) return `D${snap.ratioIndex + 1}`;
  return String(snap.gear);
}

export function Hud({
  bridge,
  spec,
  controls,
  touchSource,
}: {
  bridge: SimBridge;
  spec: CarSpec;
  controls: GameControls;
  touchSource: TouchSource;
}) {
  const t = useT();
  const fmtSpeed = useSpeedFormat();
  const [hud, setHud] = useState<HudState>({
    snap: null,
    evalUi: null,
    toasts: [],
    paused: false,
    camera: 'chase',
  });
  useEffect(() => bridge.subscribe(setHud), [bridge]);

  const snap = hud.snap;
  if (!snap) return null;
  const [speed, unit] = fmtSpeed(snap.speedKmh);
  const blinkOn = (snap.tick % 48) < 24;
  const movingNoBelt = !snap.seatbelt && snap.speedKmh > 5;
  const man = snap.manual;
  const isManual = man !== null;
  const mspec = spec.manual;
  const redline = mspec ? mspec.redlineRpm : REDLINE_RPM;
  const biteZone: [number, number] | undefined = mspec
    ? [
        Math.max(0, mspec.clutch.bitePoint - mspec.clutch.biteWidth / 2),
        Math.min(1, mspec.clutch.bitePoint + mspec.clutch.biteWidth / 2),
      ]
    : undefined;
  const gearAccent = isManual && !man.engineOn ? 'rose' : snap.gear === 'R' ? 'amber' : 'sky';

  return (
    <div className="pointer-events-none absolute inset-0 select-none">
      {/* Marcos PiP (el render 3D pinta debajo) */}
      {(snap.glance === 'left' || snap.glance === 'right' || snap.glance === 'rear') && (
        <div
          className="absolute rounded-md border-2 border-white/60 shadow-2xl"
          style={pipStyle(PIP_RECTS[snap.glance])}
        >
          <span className="absolute -top-6 left-1 rounded bg-slate-900/80 px-2 py-0.5 text-xs text-white/80">
            👁 {t(`hud.mirror.${snap.glance}`)}
          </span>
        </div>
      )}
      {snap.gear === 'R' && spec.assists.reverseCam && (
        <div
          className="absolute rounded-md border-2 border-amber-300/70 shadow-2xl"
          style={pipStyle(PIP_RECTS.reverse)}
        >
          <span className="absolute -top-6 left-1 rounded bg-slate-900/80 px-2 py-0.5 text-xs text-amber-200">
            📷 {t('hud.reverseCam')}
          </span>
          <div className="absolute inset-x-8 bottom-1 flex justify-between text-emerald-300/70">
            <span>┃</span>
            <span>┃</span>
          </div>
        </div>
      )}

      {/* Objetivos */}
      {hud.evalUi && (
        <div className="hud-objectives absolute left-3 top-3 w-72 max-w-[40vw] rounded-xl bg-slate-950/70 p-3 backdrop-blur">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-white/50">
            {t('hud.objectives')}
          </div>
          <ol className="flex flex-col gap-1 text-sm">
            {hud.evalUi.objectives.map((o) => (
              <li
                key={o.id}
                className={
                  o.status === 'done'
                    ? 'text-emerald-300/90 line-through decoration-emerald-300/40'
                    : o.status === 'active'
                      ? 'font-semibold text-white'
                      : 'text-white/35'
                }
              >
                {o.status === 'done' ? '✓ ' : o.status === 'active' ? '▸ ' : '· '}
                {t(o.titleKey)}
                {o.status === 'active' && o.hints.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {o.hints.map((h) => (
                      <span
                        key={h}
                        className="rounded bg-amber-400/20 px-1.5 py-0.5 text-[11px] font-medium text-amber-200"
                      >
                        {t(h)}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Tiempo, colisiones y caladas */}
      {hud.evalUi && (
        <div className="hud-status absolute right-3 top-3 rounded-xl bg-slate-950/70 px-3 py-2 text-right text-sm backdrop-blur">
          <div className="text-white/80">
            ⏱ {Math.floor(hud.evalUi.timeS / 60)}:{String(Math.floor(hud.evalUi.timeS % 60)).padStart(2, '0')}
          </div>
          <div className={hud.evalUi.collisions > 0 ? 'text-rose-300' : 'text-white/40'}>
            💥 {hud.evalUi.collisions}
          </div>
          {isManual && (
            <div className={man.stallCount > 0 ? 'text-amber-300' : 'text-white/40'}>
              🔻 {t('hud.stalls')}: {man.stallCount}
            </div>
          )}
        </div>
      )}

      {/* Avisos (toasts) */}
      <div className="hud-toasts absolute left-1/2 top-14 flex -translate-x-1/2 flex-col items-center gap-1.5">
        {hud.toasts.slice(-4).map(
          (toast) =>
            snap.tick - toast.bornTick < 200 && (
              <div
                key={toast.id}
                className="rounded-full bg-slate-950/85 px-4 py-1.5 text-sm font-medium text-amber-200 shadow-lg backdrop-blur"
              >
                {t(toast.key)}
              </div>
            ),
        )}
      </div>

      {/* Motor calado / arrancando */}
      {isManual && !man.engineOn && (
        <div className="absolute left-1/2 top-[30%] -translate-x-1/2">
          <div
            className={`rounded-2xl border px-6 py-4 text-center shadow-2xl backdrop-blur ${
              man.cranking
                ? 'border-amber-300/60 bg-amber-950/85'
                : 'border-rose-500/60 bg-rose-950/85'
            }`}
          >
            {man.cranking ? (
              <>
                <div className="text-xl font-black tracking-wide text-amber-300">
                  🔑 {t('hud.cranking')}
                </div>
              </>
            ) : (
              <>
                <div className="text-2xl font-black tracking-wide text-rose-300">
                  {t('hud.stalled')}
                </div>
                <div className="mt-1 max-w-64 text-sm text-white/85">{t('hud.stalledHelp')}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tablero */}
      <div className="hud-dashboard absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-4 rounded-2xl bg-slate-950/80 px-5 py-3 shadow-2xl backdrop-blur">
        {/* Selector: palanca en H (manual) o columna PRND (automático) */}
        {isManual ? (
          <HShifter
            gearCount={spec.gearRatios.length}
            current={snap.gear}
            requested={man.gearRequested}
            onSelect={(g) => touchSource.pushAction({ type: 'gear', gear: g })}
          />
        ) : (
          <div className="flex flex-col justify-center gap-0.5 text-center font-mono">
            {AUTO_GEARS.map((g) => (
              <div
                key={String(g)}
                className={`rounded px-2 text-sm font-bold leading-5 ${
                  snap.gear === g
                    ? g === 'R'
                      ? 'bg-amber-400 text-slate-950'
                      : 'bg-sky-400 text-slate-950'
                    : 'text-white/30'
                }`}
              >
                {String(g)}
              </div>
            ))}
          </div>
        )}

        {/* Tacómetro con marcha */}
        <Tacho
          rpm={snap.rpm}
          redlineRpm={redline}
          gearLabel={gearLabelOf(snap, isManual, spec.gearRatios.length > 1)}
          gearAccent={gearAccent}
          shiftUp={isManual ? man.shiftUpHint : false}
          shiftDown={isManual ? man.shiftDownHint : false}
          dimmed={isManual && !man.engineOn}
        />

        {/* Velocidad, pedales y testigos */}
        <div className="flex flex-col items-center gap-1.5 border-l border-white/10 pl-4">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-4xl font-black tabular-nums text-white">{speed}</span>
            <span className="text-xs text-white/50">{unit}</span>
          </div>
          <div className="flex items-end gap-2">
            {isManual && (
              <PedalBar
                value={man.clutchPedal}
                color="bg-amber-400"
                label={t('hud.pedal.clutch')}
                bite={biteZone}
                atBite={man.atBite}
              />
            )}
            <PedalBar value={snap.brake} color="bg-rose-400" label={t('hud.pedal.brake')} />
            <PedalBar value={snap.throttle} color="bg-emerald-400" label={t('hud.pedal.throttle')} />
            <div className="mb-4 flex flex-col items-center gap-1 pl-1">
              <div className="flex items-center gap-3 text-lg">
                <span className={snap.signalLeft && blinkOn ? 'text-emerald-400' : 'text-white/15'}>◀</span>
                <span className="text-xs text-white/40">
                  {snap.lights > 0 ? (snap.lights === 2 ? '🔵' : '💡') : ''}
                </span>
                <span className={snap.signalRight && blinkOn ? 'text-emerald-400' : 'text-white/15'}>▶</span>
              </div>
              <div className="flex max-w-40 flex-wrap justify-center gap-1">
                <Light on={snap.absActive} color="bg-amber-400/90 text-slate-950">
                  {t('hud.abs')}
                </Light>
                <Light on={snap.tcActive} color="bg-amber-400/90 text-slate-950">
                  {t('hud.tc')}
                </Light>
                <Light on={snap.hillHold} color="bg-emerald-400/90 text-slate-950">
                  {t('hud.hillhold')}
                </Light>
                <Light on={snap.handbrake} color="bg-rose-500/90 text-white">
                  (P)
                </Light>
                <Light on={movingNoBelt && blinkOn} color="bg-rose-500/90 text-white">
                  {t('hud.belt')}
                </Light>
                {isManual && (
                  <>
                    <Light on={!man.engineOn} color="bg-rose-500/90 text-white">
                      {t('hud.engine')}
                    </Light>
                    <Light
                      on={man.clutchHeat > 0.55 && blinkOn}
                      color="bg-amber-500/90 text-slate-950"
                    >
                      {t('hud.clutchHot')}
                    </Light>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <MobileControls source={touchSource} snap={snap} spec={spec} controls={controls} />

      {/* Pausa */}
      {hud.paused && (
        <div className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
          <div className="flex w-72 flex-col gap-3 rounded-2xl bg-slate-900 p-6 shadow-2xl">
            <h3 className="text-center text-2xl font-bold text-white">{t('pause.title')}</h3>
            <BigButton onClick={controls.togglePause}>{t('pause.resume')}</BigButton>
            <BigButton variant="ghost" onClick={controls.restart}>
              {t('pause.restart')}
            </BigButton>
            <BigButton variant="danger" onClick={controls.exit}>
              {t('pause.exit')}
            </BigButton>
          </div>
        </div>
      )}
    </div>
  );
}
